import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

/** 与创作页时间线一致：阻尼位移上限与触发阈值（px，已阻尼后） */
export const PULL_REFRESH_RELEASE_THRESHOLD = 52
export const PULL_REFRESH_MAX_VISUAL = 88
export const PULL_REFRESH_DAMP = 0.5

const PULL_GESTURE_CLASS = 'glyphicai-pull-gesture-active'

export type UseScrollPullToRefreshOptions = {
  scrollRef: RefObject<HTMLElement | null>
  /** ref 回调挂载后自增，首次 `current` 从 null 变为元素时再绑事件 */
  attachSignal?: number
  /** 为 false 时不挂载手势（如首屏全量骨架中） */
  enabled: boolean
  /** 请求进行中时禁止再次下拉触发 */
  refreshing: boolean
  /** 额外禁止开始下拉（如首屏初次加载） */
  isPullStartBlocked?: () => boolean
  shouldIgnorePullStart?: (target: EventTarget | null) => boolean
  /** 在「飞向中心」动画结束后调用，一般在此发起刷新请求 */
  onCommit: () => void
}

/**
 * 微信式列表下拉刷新：触摸 + 鼠标拖拽，与 {@link TimelinePullIndicator} 配套。
 */
export function useScrollPullToRefresh({
  scrollRef,
  attachSignal = 0,
  enabled,
  refreshing,
  isPullStartBlocked,
  shouldIgnorePullStart = () => false,
  onCommit,
}: UseScrollPullToRefreshOptions) {
  const [pullPx, setPullPx] = useState(0)
  const [commitFlying, setCommitFlying] = useState(false)

  const pullPxRef = useRef(0)
  const refreshingRef = useRef(refreshing)
  const commitFlyingRef = useRef(false)
  const onCommitRef = useRef(onCommit)

  useEffect(() => {
    refreshingRef.current = refreshing
  }, [refreshing])
  useEffect(() => {
    pullPxRef.current = pullPx
  }, [pullPx])
  useEffect(() => {
    onCommitRef.current = onCommit
  }, [onCommit])

  const onCommitFlyComplete = useCallback(() => {
    if (!commitFlyingRef.current) return
    commitFlyingRef.current = false
    setCommitFlying(false)
    onCommitRef.current()
  }, [])

  /** 复位位移与「飞向中心」态：超时中断、刷新开始时清空视觉 */
  const resetPullGesture = useCallback(() => {
    commitFlyingRef.current = false
    setCommitFlying(false)
    setPullPx(0)
    pullPxRef.current = 0
  }, [])

  useEffect(() => {
    if (!enabled) return
    const el = scrollRef.current
    if (!el) return

    let startY = 0
    let startX = 0
    let tracking = false
    let armed = false
    let hadArmedPull = false
    let pullSelectPinned = false
    let savedBodyUserSelect = ''
    let pullStartTarget: EventTarget | null = null
    let fromMouseGesture = false

    const resetPullVisual = () => {
      setPullPx(0)
      pullPxRef.current = 0
    }

    const pinPullSelect = () => {
      if (pullSelectPinned) return
      el.classList.add(PULL_GESTURE_CLASS)
      savedBodyUserSelect = document.body.style.userSelect
      document.body.style.userSelect = 'none'
      pullSelectPinned = true
    }

    const unpinPullSelect = () => {
      if (!pullSelectPinned) return
      el.classList.remove(PULL_GESTURE_CLASS)
      document.body.style.userSelect = savedBodyUserSelect
      pullSelectPinned = false
    }

    const beginPull = (startClientY: number, startClientX: number, target: EventTarget | null) => {
      if (commitFlyingRef.current) return false
      if (refreshingRef.current) return false
      if (isPullStartBlocked?.()) return false
      if (el.scrollTop > 0.5) return false
      startY = startClientY
      startX = startClientX
      tracking = true
      armed = false
      hadArmedPull = false
      pullStartTarget = target
      pinPullSelect()
      return true
    }

    const maybeReplayTap = (shouldCommit: boolean) => {
      if (!fromMouseGesture) return
      if (shouldCommit || hadArmedPull || !(pullStartTarget instanceof Element)) return
      const interactive = pullStartTarget.closest('a[href], button, [role="button"]')
      if (!(interactive instanceof HTMLElement)) return
      if (shouldIgnorePullStart(pullStartTarget)) return
      queueMicrotask(() => {
        interactive.click()
      })
    }

    const endPull = () => {
      if (!tracking) return
      tracking = false
      const px = pullPxRef.current
      const shouldCommit =
        armed && px >= PULL_REFRESH_RELEASE_THRESHOLD && !refreshingRef.current
      resetPullVisual()
      unpinPullSelect()
      maybeReplayTap(shouldCommit)
      pullStartTarget = null
      if (shouldCommit) {
        if (commitFlyingRef.current || refreshingRef.current) {
          armed = false
          hadArmedPull = false
          return
        }
        commitFlyingRef.current = true
        setCommitFlying(true)
      }
      armed = false
      hadArmedPull = false
      fromMouseGesture = false
    }

    const onPullMove = (clientY: number, clientX: number, ev: Event | null) => {
      if (commitFlyingRef.current) return
      if (!tracking || refreshingRef.current) return
      if (el.scrollTop > 0.5) {
        tracking = false
        armed = false
        resetPullVisual()
        unpinPullSelect()
        pullStartTarget = null
        return
      }

      const dy = clientY - startY
      const dx = clientX - startX
      if (dy <= 0) {
        if (pullPxRef.current > 0) resetPullVisual()
        return
      }

      if (!armed) {
        if (dy > 8 && dy > Math.abs(dx) * 1.05) {
          armed = true
          hadArmedPull = true
        } else if (Math.abs(dx) > dy && Math.abs(dx) > 12) {
          tracking = false
          unpinPullSelect()
          pullStartTarget = null
          return
        }
        if (!armed) return
      }

      ev?.preventDefault()

      const damped = Math.min(PULL_REFRESH_MAX_VISUAL, dy * PULL_REFRESH_DAMP)
      pullPxRef.current = damped
      setPullPx(damped)
    }

    const onTouchStart = (ev: globalThis.TouchEvent) => {
      if (shouldIgnorePullStart(ev.target)) return
      fromMouseGesture = false
      beginPull(ev.touches[0].clientY, ev.touches[0].clientX, ev.target)
    }

    const onTouchMove = (ev: globalThis.TouchEvent) => {
      onPullMove(ev.touches[0].clientY, ev.touches[0].clientX, ev)
    }

    const onTouchEnd = () => {
      endPull()
    }

    const onMouseDown = (ev: MouseEvent) => {
      if (ev.button !== 0) return
      if (shouldIgnorePullStart(ev.target)) return
      fromMouseGesture = true
      if (!beginPull(ev.clientY, ev.clientX, ev.target)) return
      /** 阻止链接默认拖拽/选区，短按由 endPull 回放 click */
      ev.preventDefault()
    }

    const onWindowMouseMove = (ev: MouseEvent) => {
      onPullMove(ev.clientY, ev.clientX, ev)
    }

    const onWindowMouseUp = () => {
      endPull()
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true, capture: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false, capture: true })
    el.addEventListener('touchend', onTouchEnd, { capture: true })
    el.addEventListener('touchcancel', onTouchEnd, { capture: true })
    el.addEventListener('mousedown', onMouseDown, { capture: true })
    window.addEventListener('mousemove', onWindowMouseMove, { passive: false, capture: true })
    window.addEventListener('mouseup', onWindowMouseUp, { capture: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart, true)
      el.removeEventListener('touchmove', onTouchMove, true)
      el.removeEventListener('touchend', onTouchEnd, true)
      el.removeEventListener('touchcancel', onTouchEnd, true)
      el.removeEventListener('mousedown', onMouseDown, true)
      window.removeEventListener('mousemove', onWindowMouseMove, true)
      window.removeEventListener('mouseup', onWindowMouseUp, true)
      unpinPullSelect()
      resetPullVisual()
      tracking = false
      armed = false
      hadArmedPull = false
      pullStartTarget = null
      fromMouseGesture = false
    }
  }, [attachSignal, enabled, isPullStartBlocked, scrollRef, shouldIgnorePullStart])

  return {
    pullPx,
    commitFlying,
    onCommitFlyComplete,
    resetPullGesture,
    releaseThreshold: PULL_REFRESH_RELEASE_THRESHOLD,
  }
}
